import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { BrandService } from 'src/app/services/brand.service';
import { CategoryService } from 'src/app/services/category.service';
import { ProductService } from 'src/app/services/product.service';
import { SapCategoryService } from 'src/app/services/sap-category.service';

@Component({
  standalone: false,
  selector: 'app-products-detail',
  templateUrl: './products-detail.component.html',
})
export class ProductsDetailComponent implements OnInit {
  id = '';
  isEditMode = false;
  validationTriggered = false;
  item: any = null;
  form: any = {};
  brands: any[] = [];
  categories: any[] = [];
  sapCategories: any[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private productService: ProductService,
    private brandService: BrandService,
    private categoryService: CategoryService,
    private sapCategoryService: SapCategoryService,
    private toaster: ToastrService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.id = this.route.snapshot.paramMap.get('id') ?? '';
    this.brandService.list().subscribe({
      next: (result) => {
        this.brands = result?.data?.brands ?? [];
        this.cdr.detectChanges();
      },
    });
    this.categoryService.list().subscribe({
      next: (result) => {
        this.categories = result?.data?.categories ?? [];
        if (this.item) {
          this.resetForm();
        }
        this.cdr.detectChanges();
      },
    });
    this.sapCategoryService.list().subscribe({
      next: (result) => {
        this.sapCategories = result?.data?.sap_categories ?? [];
        this.cdr.detectChanges();
      },
    });
    this.load();
  }

  load(): void {
    this.productService.get(this.id).subscribe({
      next: (result) => {
        this.item = result.data;
        this.resetForm();
        this.isEditMode = false;
        this.validationTriggered = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.toaster.error('Product not found.');
        this.router.navigate(['/ecomm/products']);
      },
    });
  }

  resetForm(): void {
    this.form = {
      brand_id: this.item.brand_id,
      group_id: this.item.group_id ?? '',
      stock_id: this.item.stock_id,
      barcode: this.item.barcode,
      sku: this.item.sku,
      name: this.item.name,
      base_price: this.item.base_price,
      description: this.item.description ?? '',
      special_remarks: this.item.special_remarks ?? '',
      status: this.item.status,
      category_key: this.item?.category_ids?.length ? `c:${this.item.category_ids[0]}` : '',
    };
  }

  startEdit(): void {
    this.resetForm();
    this.isEditMode = true;
    this.validationTriggered = false;
  }

  cancelEdit(): void {
    this.resetForm();
    this.isEditMode = false;
    this.validationTriggered = false;
  }

  save(): void {
    this.validationTriggered = true;
    const required = ['brand_id', 'stock_id', 'barcode', 'sku', 'name', 'base_price'];
    if (required.some((key) => this.form[key] === '' || this.form[key] === null || this.form[key] === undefined)) {
      this.toaster.error('Please fill in all required fields.');
      return;
    }

    const categoryIds = this.resolveCategoryIds(this.form.category_key);
    if (categoryIds === null) {
      return;
    }

    const payload = {
      brand_id: Number(this.form.brand_id),
      group_id: this.form.group_id === '' ? null : Number(this.form.group_id),
      stock_id: this.form.stock_id,
      barcode: this.form.barcode,
      sku: this.form.sku,
      name: this.form.name,
      base_price: Number(this.form.base_price),
      description: this.form.description || null,
      special_remarks: this.form.special_remarks || null,
      status: Number(this.form.status),
      category_ids: categoryIds,
    };

    this.productService.update(this.id, payload).subscribe({
      next: (result) => {
        this.item = result.data;
        this.isEditMode = false;
        this.validationTriggered = false;
        this.toaster.success('Product updated.');
        this.cdr.detectChanges();
      },
      error: (error) => {
        this.toaster.error(error?.error?.message || 'Failed to update product.');
      },
    });
  }

  statusLabel(status: number): string {
    return status === 1 ? 'Active' : status === 2 ? 'Inactive' : String(status ?? '-');
  }

  categoryNames(): string {
    if (!this.item?.category_ids?.length) {
      return this.item?.category?.name || '-';
    }
    return this.categories
      .filter((c) => this.item.category_ids.includes(c.id))
      .map((c) => c.name)
      .join(', ') || (this.item?.category?.name || '-');
  }

  private resolveCategoryIds(categoryKey: string): number[] | null {
    if (!categoryKey) {
      return [];
    }

    if (categoryKey.startsWith('c:')) {
      const id = Number(categoryKey.slice(2));
      return Number.isFinite(id) ? [id] : [];
    }

    if (categoryKey.startsWith('s:')) {
      const sapId = Number(categoryKey.slice(2));
      const sap = this.sapCategories.find((c) => c.id === sapId);
      if (!sap) {
        this.toaster.error('Selected SAP category was not found.');
        return null;
      }
      const match = this.categories.find(
        (c) => String(c.name).trim().toLowerCase() === String(sap.name).trim().toLowerCase(),
      );
      if (!match) {
        this.toaster.error(
          `No Category named "${sap.name}". Create it under Categories, or pick a Category.`,
        );
        return null;
      }
      return [match.id];
    }

    return [];
  }
}
